import { useMemo, useRef, useState } from "react";

import {

  ComposedChart, Line, Scatter, XAxis, YAxis, CartesianGrid,

  Tooltip, ResponsiveContainer,

} from "recharts";

import { C } from "./shared";
 
/* ============================================================

   KNOBS

   ============================================================ */
 
// Category benchmarks (your B1–B5 numbers, tpd at the fleet design point)

const BENCHMARKS = { B1: 13.663, B2: 24.266, B3: 29.95, B4: 32.813, B5: 56.203 };
 
// Fallback ONLY — used if DWT_CATAGORY_TYPE ("dwt") doesn't already resolve to

// a B1–B5 label. A vessel falls in the first band whose maxDwt it doesn't exceed.

// >>> replace with your real DWT thresholds if you need the fallback <<<

const CATEGORY_BANDS = [

  { cat: "B1", minDwt: 0, maxDwt: 30000 },

  { cat: "B2", minDwt: 30000, maxDwt: 55000 },

  { cat: "B3", minDwt: 55000, maxDwt: 80000 },

  { cat: "B4", minDwt: 80000, maxDwt: 150000 },

  { cat: "B5", minDwt: 150000, maxDwt: 400000 },

];
 
/* ============================================================

   Color gradient: red (over benchmark) → yellow → green (under)

   ============================================================ */

const getVesselColor = (deltaPct) => {
  // deltaPct > 0 = above benchmark (red shades)
  // deltaPct < 0 = below benchmark (green shades)
  // deltaPct ≈ 0 = at benchmark (yellow)
  
  if (deltaPct > 40) return "#7f1d1d";      // Very high (dark red)
  if (deltaPct > 30) return "#991b1b";      // High (red)
  if (deltaPct > 20) return "#dc2626";      // Above (bright red)
  if (deltaPct > 10) return "#f97316";      // Slightly above (orange)
  if (deltaPct > 0) return "#eab308";       // Just above (yellow-orange)
  
  if (deltaPct > -10) return "#fbbf24";     // Just below (light yellow)
  if (deltaPct > -20) return "#86efac";     // Below (light green)
  if (deltaPct > -30) return "#22c55e";     // Lower (green)
  if (deltaPct > -40) return "#10b981";     // Much lower (darker green)
  
  return "#047857";                         // Very low (dark green)
};

/* ============================================================

   Derived + helpers

   ============================================================ */
 
const CAT_ORDER = Object.keys(BENCHMARKS);                 // ["B1" ... "B5"]

const CAT_INDEX = Object.fromEntries(CAT_ORDER.map((c, i) => [c, i]));

const BAND_BY_CAT = Object.fromEntries(CATEGORY_BANDS.map((b) => [b.cat, b]));

const BENCH = CAT_ORDER.map((c, i) => ({

  cat: c,

  xStart: i,

  xEnd: i + 1,

  benchmark: BENCHMARKS[c],

}));
 
// Create step-wise benchmark data (start from Y-axis, then step)

const BUILD_STEP_BENCH = (bench = BENCH) => {

  const steps = [];
 
  bench.forEach((b, i) => {

    if (i === 0) {

      // B1 starts at x=0 and stays flat until the first boundary.

      steps.push({ x: b.xStart, benchmark: b.benchmark, cat: b.cat, isStep: true });

      steps.push({ x: b.xEnd, benchmark: b.benchmark, cat: b.cat, isStep: true });

      return;

    }
 
    const prev = bench[i - 1];

    // Vertical transition at category boundary, then horizontal in new band.

    steps.push({ x: b.xStart, benchmark: prev.benchmark, cat: prev.cat, isStep: true });

    steps.push({ x: b.xStart, benchmark: b.benchmark, cat: b.cat, isStep: true });

    steps.push({ x: b.xEnd, benchmark: b.benchmark, cat: b.cat, isStep: true });

  });

  return steps;

};
 
const BENCH_STEPS = BUILD_STEP_BENCH();
 
const first = (v) => (Array.isArray(v) ? v[0] : v);
 
// Pull deadweight + category-type out of the run response's draught_curves.

function getDwtInfo(vessel) {

  const curves = vessel?.data?.draught_curves || {};

  const c = Object.values(curves)[0];

  if (!c) return { dwt: null, catType: null };

  const dwt = Number(first(c.DWT));

  const catType = first(c.dwt);   // DWT_CATAGORY_TYPE

  return { dwt: Number.isFinite(dwt) ? dwt : null, catType: catType ?? null };

}
 
// Use the backend's category type directly if it's already B1–B5 or 1–5.

function normalizeCatType(raw) {

  if (raw == null) return null;

  const s = String(raw).trim().toUpperCase();

  if (BENCHMARKS[s] != null) return s;                     // "B3" -> B3

  const n = Number(s);

  if (Number.isInteger(n) && n >= 1 && n <= CAT_ORDER.length) return CAT_ORDER[n - 1]; // 3 -> B3

  return null;

}
 
function resolveCategory(dwt, catType) {

  const fromType = normalizeCatType(catType);

  if (fromType) return fromType;

  if (dwt == null) return null;

  for (const b of CATEGORY_BANDS) if (dwt <= b.maxDwt) return b.cat;

  return CAT_ORDER[CAT_ORDER.length - 1];

}
 
function xWithinCategoryBand(cat, dwt) {

  const i = CAT_INDEX[cat];

  if (!Number.isInteger(i)) return null;
 
  const band = BAND_BY_CAT[cat];

  if (!band || !Number.isFinite(dwt)) return i + 0.5;
 
  const span = Number(band.maxDwt) - Number(band.minDwt);

  if (!Number.isFinite(span) || span <= 0) return i + 0.5;
 
  const rawRatio = (Number(dwt) - Number(band.minDwt)) / span;

  // Keep points visibly inside the band and avoid exact overlap on boundaries.

  const ratio = Math.max(0.02, Math.min(0.98, rawRatio));

  return i + ratio;

}
 
function spreadOverlappingPoints(points) {

  const byKey = {};

  points.forEach((p) => {

    const key = `${p.cat}|${Number(p.x).toFixed(4)}|${Number(p.tpd).toFixed(3)}`;

    (byKey[key] ||= []).push(p);

  });
 
  const out = [];

  Object.values(byKey).forEach((group) => {

    if (group.length === 1) {

      out.push(group[0]);

      return;

    }
 
    const sorted = [...group].sort((a, b) => String(a.imo).localeCompare(String(b.imo)));

    const step = 0.02;
 
    sorted.forEach((p, i) => {

      const bandStart = CAT_INDEX[p.cat];

      const bandEnd = bandStart + 1;

      const offset = (i - (sorted.length - 1) / 2) * step;

      const x = Math.max(bandStart + 0.01, Math.min(bandEnd - 0.01, p.x + offset));

      out.push({ ...p, x });

    });

  });
 
  return out;

}
 
// Resolve design operating point per vessel.

function vesselRef(vessel) {

  const fr = vessel?.fleet_reference || {};

  const d = Number(

    fr.fleet_draught ??

    fr.design_draught ??

    vessel?.data?.fleet_draught ??

    vessel?.data?.design_draught ??

    vessel?.data?.DESIGN_DRAUGHT

  );

  const s = Number(

    fr.fleet_speed ??

    fr.design_speed ??

    vessel?.data?.fleet_speed ??

    vessel?.data?.design_speed ??

    vessel?.data?.DESIGN_SPEED

  );

  return { draught: Number.isFinite(d) ? d : null, speed: Number.isFinite(s) ? s : null };

}
 
function nearestIndex(arr, target) {

  let best = 0, bestD = Infinity;

  arr.forEach((v, i) => {

    const d = Math.abs(Number(v) - target);

    if (d < bestD) { bestD = d; best = i; }

  });

  return best;

}
 
// tpd for one vessel at that vessel's design draught + speed (falls back to

// highest draught / highest speed if the reference point is missing).

function tpdAtDesign(fuelData, ref) {

  const recs = Object.values(fuelData || {}).filter(

    (r) => Array.isArray(r?.speed) && Array.isArray(r?.fuel_t_per_day) && r.speed.length

  );

  if (!recs.length) return null;
 
  let rec;

  if (ref.draught != null) {

    rec = recs.reduce((a, b) =>

      Math.abs(Number(b.draught) - ref.draught) < Math.abs(Number(a.draught) - ref.draught) ? b : a

    );

  } else {

    rec = recs.reduce((a, b) => (Number(b.draught) > Number(a.draught) ? b : a));

  }
 
  const targetSpeed = ref.speed != null ? ref.speed : Math.max(...rec.speed.map(Number));

  const v = rec.fuel_t_per_day[nearestIndex(rec.speed, targetSpeed)];

  return Number.isFinite(v) ? v : null;

}
 
// tpd for one vessel at an ARBITRARY (draught, speed) operating point.

// Same nearest-neighbour logic as tpdAtDesign, but the point comes from the

// user's dropdown selection instead of the vessel's design reference.

function tpdAtPoint(fuelData, draught, speed) {

  const recs = Object.values(fuelData || {}).filter(

    (r) => Array.isArray(r?.speed) && Array.isArray(r?.fuel_t_per_day) && r.speed.length

  );

  if (!recs.length) return null;
 
  const d = Number(draught);

  let rec;

  if (Number.isFinite(d)) {

    rec = recs.reduce((a, b) =>

      Math.abs(Number(b.draught) - d) < Math.abs(Number(a.draught) - d) ? b : a

    );

  } else {

    rec = recs.reduce((a, b) => (Number(b.draught) > Number(a.draught) ? b : a));

  }
 
  const s = Number(speed);

  const targetSpeed = Number.isFinite(s) ? s : Math.max(...rec.speed.map(Number));

  const val = rec.fuel_t_per_day[nearestIndex(rec.speed, targetSpeed)];

  return Number.isFinite(val) ? val : null;

}
 
// Fleet minimum design draught/speed across the analysed vessels.

function fleetMinRef(list) {

  let minD = Infinity;

  let minS = Infinity;

  list.forEach((v) => {

    const { draught, speed } = vesselRef(v);

    if (Number.isFinite(draught)) minD = Math.min(minD, draught);

    if (Number.isFinite(speed)) minS = Math.min(minS, speed);

  });

  return {

    draught: Number.isFinite(minD) ? minD : null,

    speed: Number.isFinite(minS) ? minS : null,

  };

}
 
// [min, min-1, ... , min-5], dropping any non-positive value.

function buildDescendingOptions(minVal, count = 5) {

  if (!Number.isFinite(minVal)) return [];

  const out = [];

  for (let i = 0; i <= count ; i++) {

    const val = Math.round((minVal - i) * 100) / 100;

    if (val <= 0) break;

    out.push(val);

  }

  return out;

}

// [min-5 ... min ... min+5], dropping any non-positive value.
function buildSpeedOptions(centerVal, down = 5, up = 5) {

  if (!Number.isFinite(centerVal)) return [];

  const out = [];

  for (let i = down; i >= 1; i--) {

    const val = Math.round((centerVal - i) * 100) / 100;

    if (val > 0) out.push(val);

  }

  out.push(Math.round(centerVal * 100) / 100);

  for (let i = 1; i <= up; i++) {

    const val = Math.round((centerVal + i) * 100) / 100;

    if (val > 0) out.push(val);

  }

  return out;

}
 
const fmtNum = (n) => (Number.isInteger(n) ? String(n) : String(Number(Number(n).toFixed(2))));
 
function CustomTooltip({ active, payload }) {

  if (!active || !payload?.length) return null;

  // Get the first payload entry

  let p = null;

  // Try to find vessel IMO entry

  const vesselEntry = payload.find((entry) => entry?.payload?.imo);

  if (vesselEntry) {

    p = vesselEntry.payload;

  }

  // If no vessel entry found, return null (don't show tooltip for benchmark or empty)

  if (!p || !p.imo) return null;

  const box = {

    background: "white",

    border: `1px solid #e5e7eb`,

    borderRadius: 8,

    padding: "12px 14px",

    fontSize: 11,

    color: "#1f2937",

    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",

  };
 
  return (
<div style={box}>
<div style={{ color: "#1f2937", fontWeight: 700, marginBottom: 6 }}>

        IMO {p.imo}
</div>
<div style={{ marginBottom: 4 }}>
<span style={{ color: "#6b7280" }}>Category: </span>
<span style={{ color: "#3b82f6", fontWeight: 600 }}>{p.cat}</span>

        {p.dwt ? ` · ${Math.round(p.dwt).toLocaleString()} DWT` : ""}
</div>
<div style={{ marginBottom: 4 }}>
<span style={{ color: "#6b7280" }}>Consumption: </span>
<span style={{ fontWeight: 600 }}>{p.tpd?.toFixed(2) || "N/A"} tpd</span>
</div>
<div style={{ marginBottom: 6 }}>
<span style={{ color: "#6b7280" }}>Benchmark: </span>
<span style={{ fontWeight: 600 }}>{p.benchmark?.toFixed(2) || "N/A"} tpd</span>

</div>

<div style={{ marginBottom: 6 }}>
<span style={{ color: "#6b7280" }}>Built Year: </span>
<span style={{ fontWeight: 600 }}>{p.built_year || "N/A"}</span>
</div>
<div style={{

        color: p.over ? "#dc2626" : "#059669",

        fontWeight: 700,

        fontSize: 10,

      }}>

        {p.over ? "+" : ""}{p.deltaPct || 0}% vs benchmark
</div>
</div>

  );

}
 
/* ============================================================

   Component

   ============================================================ */
 
function FleetBenchmark({ ok = [], fuelByImo = {}, builtYearByImo = {} }) {

  const [showBench, setShowBench] = useState(true);

  const [hover, setHover] = useState(null); // { point, cx, cy }
 
  // Operating-point override. "" = use each vessel's own design point (default,

  // identical to the previous behaviour). A numeric value overrides that axis.

  const [selDraught, setSelDraught] = useState("");

  const [selSpeed, setSelSpeed] = useState("");
 
  // Fleet minimum design draught/speed -> tops of the dropdowns.

  const fleetMin = useMemo(() => fleetMinRef(ok), [ok]);

  const draughtOptions = useMemo(() => buildDescendingOptions(fleetMin.draught), [fleetMin.draught]);

  const speedOptions = useMemo(() => buildSpeedOptions(fleetMin.speed), [fleetMin.speed]);
 
  const overrideActive = selDraught !== "" || selSpeed !== "";
 
  const { vessels, skipped } = useMemo(() => {

    const resolved = [];

    const missing = [];
 
    ok.forEach((v) => {

      const { dwt, catType } = getDwtInfo(v);

      const cat = resolveCategory(dwt, catType);

      const ref = vesselRef(v);

      // Selected value wins per-axis; an axis left on "Design" keeps the

      // vessel's own design value, so a partial selection still makes sense.

      const useDraught = selDraught !== "" ? Number(selDraught) : ref.draught;

      const useSpeed = selSpeed !== "" ? Number(selSpeed) : ref.speed;

      const tpd = overrideActive

        ? tpdAtPoint(fuelByImo[v.imo], useDraught, useSpeed)

        : tpdAtDesign(fuelByImo[v.imo], ref);

      const benchmark = cat ? BENCHMARKS[cat] : null;
 
      if (cat == null || tpd == null || benchmark == null) {

        missing.push({ imo: v.imo, noCat: cat == null, noTpd: tpd == null });

        return;

      }

      resolved.push({ imo: v.imo, dwt, cat, tpd, benchmark, built_year: builtYearByImo[v.imo] });

    });
 
    const baseVessels = resolved.map((r) => {

      const deltaPct = r.benchmark > 0 ? Math.round(((r.tpd - r.benchmark) / r.benchmark) * 1000) / 10 : 0;

      return {

        ...r,

        x: xWithinCategoryBand(r.cat, r.dwt),

        over: r.tpd > r.benchmark,

        deltaPct,

        color: getVesselColor(deltaPct),

      };

    });
 
    const vessels = spreadOverlappingPoints(baseVessels);
 
    return { vessels, skipped: missing };

  }, [ok, fuelByImo, builtYearByImo, selDraught, selSpeed, overrideActive]);
 


  const VesselDot = (props) => {

    const { cx, cy, payload } = props;

    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;

    const active = hover?.point?.imo === payload?.imo;

    const color = payload?.color || "#999999";  // Use gradient color from vessel data

    return (
<g>

        {/* transparent, slightly larger hit area for easier hovering */}
<circle

          cx={cx} cy={cy} r={11}

          fill="transparent"

          style={{ cursor: "pointer" }}

          onMouseEnter={() => setHover({ point: payload, cx, cy })}

          onMouseLeave={() => setHover(null)}

        />
<circle

          cx={cx} cy={cy} r={active ? 6.5 : 5}

          fill={color}

          stroke="#fff" strokeWidth={1.5}

          pointerEvents="none"

        />
</g>

    );

  };
 
  const maxY = Math.max(0, ...BENCH.map((b) => b.benchmark), ...vessels.map((v) => v.tpd));

  const yMax = Math.ceil((maxY + 5) / 5) * 5 || 10;

  // Calculate zoom domain to focus only on categories with vessels
  const zoomDomain = useMemo(() => {
    if (vessels.length === 0) {
      return { xMin: 0, xMax: CAT_ORDER.length, yMin: 0, yMax: 10, categoryRange: null };
    }

    // Get all unique categories with vessels
    const categoriesWithVessels = new Set(vessels.map((v) => v.cat));
    const catIndices = Array.from(categoriesWithVessels)
      .map((cat) => CAT_ORDER.indexOf(cat))
      .filter((idx) => idx !== -1)
      .sort((a, b) => a - b);

    if (catIndices.length === 0) {
      return { xMin: 0, xMax: CAT_ORDER.length, yMin: 0, yMax: 10, categoryRange: null };
    }

    const minCatIdx = catIndices[0];
    const maxCatIdx = catIndices[catIndices.length - 1];

    // X-axis spans exactly from the lower limit of the first category to the
    // upper limit of the last one (intermediate categories included).
    const xMin = minCatIdx;
    const xMax = maxCatIdx + 1;

    // Get vessels only in visible categories
    const visibleVessels = vessels.filter((v) => {
      const idx = CAT_ORDER.indexOf(v.cat);
      return idx >= minCatIdx && idx <= maxCatIdx;
    });

    const visibleBenchmarks = BENCH.slice(minCatIdx, maxCatIdx + 1).map((b) => b.benchmark);
    const benchmarkCenter = visibleBenchmarks.reduce((sum, value) => sum + value, 0) / visibleBenchmarks.length;
    const maxDeviation = Math.max(
      ...visibleVessels.map((v) => Math.abs(v.tpd - benchmarkCenter)),
      ...visibleBenchmarks.map((value) => Math.abs(value - benchmarkCenter))
    );

    // Keep the visible benchmark levels near the vertical center while including vessel outliers.
    const yHalfRange = Math.max(maxDeviation * 1.1, 5);
    const yMin = benchmarkCenter - yHalfRange;
    const yMaxZoomed = benchmarkCenter + yHalfRange;

    // Ticks: every band boundary (DWT limits) + every band center (label)
    const focusedTicks = [];
    for (let i = minCatIdx; i <= maxCatIdx; i++) {
      focusedTicks.push(i);       // Lower limit of category i
      focusedTicks.push(i + 0.5); // Category center
    }
    focusedTicks.push(maxCatIdx + 1); // Upper limit of last category

    // Benchmark steps only for the visible categories
    const benchSteps = BUILD_STEP_BENCH(BENCH.slice(minCatIdx, maxCatIdx + 1));

    return { xMin, xMax, yMin, yMax: yMaxZoomed, categoryRange: { minCatIdx, maxCatIdx }, focusedTicks, benchSteps };
  }, [vessels]);

  const axisDomain = [zoomDomain.xMin, zoomDomain.xMax];
  const axisTicks = zoomDomain.focusedTicks || [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
  const yAxisDomain = [zoomDomain.yMin, zoomDomain.yMax];
  const benchSteps = zoomDomain.benchSteps || BENCH_STEPS;
 
  return (
<div style={{ background: "white", border: `1px solid ${C.borderCard}`, borderRadius: 14, padding: 16, height: "100%", minHeight: 500, display: "flex", flexDirection: "column" }}>

      {/* Header */}
<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
<div>
<div style={{ fontSize: 13, color: "black", fontWeight: 700 }}>

            Fleet Consumption vs Category Benchmark
</div>
<div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>

            Fleet consumption benchmark comparison · one point per vessel
</div>
</div>
<div style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }} onClick={() => setShowBench((s) => !s)}>
<div style={{ width: 32, height: 18, borderRadius: 9, position: "relative", background: showBench ? C.accent : "rgba(255,255,255,0.12)", transition: "background .2s" }}>
<div style={{ position: "absolute", top: 2, left: showBench ? 16 : 2, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left .2s" }} />
</div>
<span style={{ fontSize: 11, color: C.textSecondary }}>Benchmark line</span>
</div>
</div>
 
      {vessels.length === 0 ? (
<div style={{ padding: "28px 12px", textAlign: "center", color: C.textMuted, fontSize: 12, border: `1px dashed ${C.border}`, borderRadius: 10 }}>

          Nothing to plot yet — waiting on fuel data and a resolvable category per vessel.

          {skipped.some((s) => s.noCat) && (
<div style={{ marginTop: 8, color: C.warning }}>

              Some vessels have no category — confirm draught_curves now carry DWT / DWT_CATAGORY_TYPE (redeploy backend), or set the DWT bands in FleetBenchmark.jsx.
</div>

          )}
</div>

      ) : (
<>

          {/* Legend */}
<div style={{ display: "flex", gap: 18, marginBottom: 12, flexWrap: "wrap" }}>
<span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textSecondary }}>
<span style={{ width: 20, height: 2, background: C.accent, display: "inline-block", borderRadius: 2 }} /> Fleet Avg Fuel Consumption
</span>
<span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textSecondary }}>
  <span style={{ fontSize: 10, color: "#6b7280" }}>Color band: </span>
  <span
    aria-label="Far above to far below benchmark"
    style={{
      width: 96,
      height: 8,
      display: "inline-block",
      borderRadius: 4,
      background: "linear-gradient(90deg, #7f1d1d 0%, #dc2626 24%, #eab308 48%, #22c55e 72%, #047857 100%)",
    }}
  />
  <span style={{ fontSize: 11 , fontWeight: 400 }}> Far above → Above → At → Below → Far below benchmark</span>
</span>
</div>
 
         
 
          {/* Operating-point selectors: fleet minimum design value down to (min - 5) */}
<div style={{

            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,

            marginBottom: 12, padding: "10px 12px",

            background: "#f8fafc", border: `1px solid ${C.borderCard}`, borderRadius: 10,

          }}>
<span style={{ fontSize: 11, fontWeight: 700, color: C.textSecondary, letterSpacing: "0.04em" }}>

              OPERATING POINT
</span>
 
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMuted }}>

              Draught
<select

                value={selDraught}

                onChange={(e) => setSelDraught(e.target.value)}

                style={{ padding: "6px 8px", borderRadius: 8, background: "white", border: `1px solid ${C.border}`, color: "black", fontSize: 12 }}
>
<option value="">Design</option>

                {draughtOptions.map((d) => (
<option key={d} value={d}>{fmtNum(d)} m</option>

                ))}
</select>
</label>
 
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMuted }}>

              Speed
<select

                value={selSpeed}

                onChange={(e) => setSelSpeed(e.target.value)}

                style={{ padding: "6px 8px", borderRadius: 8, background: "white", border: `1px solid ${C.border}`, color: "black", fontSize: 12 }}
>
<option value="">Design</option>

                {speedOptions.map((s) => (
<option key={s} value={s}>{fmtNum(s)} kn</option>

                ))}
</select>
</label>
 
            {overrideActive && (
<button

                onClick={() => { setSelDraught(""); setSelSpeed(""); }}

                style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: "white", color: C.accent, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
>

                Reset to design
</button>

            )}
 
            <span style={{ marginLeft: "auto", fontSize: 11, color: C.textMuted }}>

              {overrideActive

                ? `Showing ${selDraught !== "" ? `${fmtNum(Number(selDraught))} m` : "design draught"} · ${selSpeed !== "" ? `${fmtNum(Number(selSpeed))} kn` : "design speed"}`

                : "Showing each vessel at its design point"} <br /> <br />

              {fleetMin.draught != null && (
<> Fleet min design {fmtNum(fleetMin.draught)} m{fleetMin.speed != null ? ` / ${fmtNum(fleetMin.speed)} kn` : ""}</>

              )}
</span>
</div>
 
          <div style={{ position: "relative", width: "100%" }}>
<ResponsiveContainer width="100%" height={360}>
<ComposedChart margin={{ top: 10, right: 20, bottom: 24, left: 6 }}>
<CartesianGrid stroke="#e5e7eb" strokeDasharray="4 3" vertical={false} />
<XAxis

                  type="number" dataKey="x"

                  domain={axisDomain}

                  allowDataOverflow

                  ticks={axisTicks}

                  tickFormatter={(x) => {

                    // Show category names at centers (0.5, 1.5, 2.5, 3.5, 4.5)

                    if (x % 1 === 0.5) {

                      const catIndex = Math.floor(x);

                      return CAT_ORDER[catIndex] || "";

                    }

                    // Show DWT limits at boundaries (0 = B1 lower ... 5 = B5 upper)

                    if (Number.isInteger(x)) {

                      if (x < CATEGORY_BANDS.length) return CATEGORY_BANDS[x].minDwt.toLocaleString();

                      const last = CATEGORY_BANDS[CATEGORY_BANDS.length - 1];

                      return x === CATEGORY_BANDS.length && last ? last.maxDwt.toLocaleString() : "";

                    }

                    return "";

                  }}

                  tick={{ fontSize: 10, fill: C.textSecondary }}

                  label={{ value: "DWT Category Bands", position: "insideBottom", offset: -10, fontSize: 12, fill: C.textMuted }}

                />
<YAxis

                  type="number" domain={yAxisDomain} tickCount={5}
                  tickFormatter={(value) => Number(value).toFixed(1)}

                  tick={{ fontSize: 10, fill: C.textMuted }} width={64}

                  label={{ value: "Consumption (tpd)", angle: -90, position: "insideLeft", fontSize: 12, fill: C.textMuted, offset: 12 }}

                />

                {/* recharts <Tooltip> removed on purpose — hover is driven by VesselDot */}

                {showBench && (

                  <Line

                    data={benchSteps} dataKey="benchmark" stroke={C.accent}

                    strokeWidth={2} strokeDasharray="6 4"

                    dot={false} isAnimationActive={false} name="benchmark"

                  />

                )}

                <Scatter

                  data={vessels} dataKey="tpd"

                  shape={<VesselDot />}

                  isAnimationActive={false} name="Vessels"

                />

            </ComposedChart>
</ResponsiveContainer>

            {hover && (
<div

                style={{

                  position: "absolute",

                  left: hover.cx,

                  top: hover.cy,

                  transform: hover.cy < 130

                    ? "translate(-50%, 14px)"           // flip below when near the top

                    : "translate(-50%, calc(-100% - 12px))",

                  pointerEvents: "none",

                  zIndex: 20,

                }}
>
<CustomTooltip active payload={[{ payload: hover.point }]} />
</div>

            )}
</div>
 
          {skipped.length > 0 && (
<div style={{ marginTop: 8, fontSize: 11, color: C.warning }}>

              {skipped.length} vessel(s) not plotted (missing category or fuel data):{" "}

              {skipped.map((s) => s.imo).join(", ")}
</div>

          )}
</>

      )}
</div>

  );

}
 
export default FleetBenchmark;
 